import { apiFetch } from '@/api/apiFetch';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle, User, Building2, Coins, Check, Circle } from 'lucide-react';
import AuthShell, { labelStyle, inputStyle, iconStyle } from '@/components/auth/AuthShell';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import { LEGAL_VERSION, LEGAL_URLS } from '@/lib/legal';

// Multidivisa Wave A — divisas soportadas como moneda funcional. La moneda
// funcional SOLO se elige aquí, en el alta: tras el primer asiento queda
// bloqueada (CURRENCY_LOCKED en el backend). Mantener sincronizado con
// CURRENCIES de AccountingSettingsTab.jsx.
const FUNCTIONAL_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'];

// Requisitos de contraseña. Espejo EXACTO de `fields.password`
// (`verix-api/src/utils/schemas.ts`): si cambian allí, cambian aquí.
//
// Mientras esto no existía, el formulario solo prometía "Mínimo 8 caracteres" y
// el backend rechazaba con 422 `Validation error` —así, en inglés y sin decir
// qué campo—, que es lo único que veía quien se daba de alta. Un alta perdida
// por una contraseña sin símbolo, sin forma de saberlo.
//
// El máximo de 128 no entra en la lista: lo impone el `maxLength` del input, así
// que no es un requisito que el usuario pueda incumplir.
const PASSWORD_RULES = [
  { id: 'length', test: (v) => v.length >= 8,          defaultLabel: 'Al menos 8 caracteres' },
  { id: 'upper',  test: (v) => /[A-Z]/.test(v),        defaultLabel: 'Una mayúscula' },
  { id: 'lower',  test: (v) => /[a-z]/.test(v),        defaultLabel: 'Una minúscula' },
  { id: 'digit',  test: (v) => /[0-9]/.test(v),        defaultLabel: 'Un número' },
  { id: 'symbol', test: (v) => /[^A-Za-z0-9]/.test(v), defaultLabel: 'Un símbolo (!, ?, #…)' },
];

export default function Register() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  // Sprint 5.bis.0.5 — accounting-firm invitation flow. When this param is
  // present, the user is registering as an advisor (Flow A → B atomic):
  // backend's /auth/register?invitation_token=X creates the user + firm +
  // member + companies.accounting_firm_id link in a single $transaction.
  // The legacy `token` body field is not required in this path.
  const invitationToken = searchParams.get('invitation_token') || '';
  const isAccountingFirmInvite = !!invitationToken && !token;
  const emailFromUrl = searchParams.get('email') || '';

  const isJoin = searchParams.get('join') === '1' || isAccountingFirmInvite;
  // Alta pública self-service: crea empresa nueva con prueba de 30 días (90 si el
  // correo estaba en la lista de espera) vía POST /auth/register-promo.
  //
  // Es el caso POR DEFECTO desde que la campaña abrió (15-09-2026): cualquier
  // /register sin token de invitación es un alta pública. Antes hacía falta
  // `?promo=lanzamiento` y quien tecleaba /register a pelo se topaba con una
  // pantalla de «solo por invitación» que ya no describía la realidad. El
  // parámetro sigue siendo inocuo: la landing lo manda y no molesta.
  //
  // Quién puede darse de alta lo decide el backend, no esta pantalla:
  // `promoLaunchClosedReason` (PROMO_LAUNCH_ENABLED + ventana) contesta 403 al
  // enviar si la campaña está cerrada. No hay señal pública de ese estado, así
  // que el formulario se pinta igual y el motivo llega en el error.
  const isPromo = !token && !invitationToken;
  // hp_url = honeypot anti-bot: campo oculto que siempre debe ir vacío.
  // El CIF NO está aquí a propósito: se pregunta en /bienvenida (paso `identity`),
  // que es el único sitio que valida el dígito de control y ofrece la corrección
  // ("¿Quisiste decir B12345674?"). Mientras se pedía aquí, ese paso —el único
  // que puede bloquear el embudo— no lo veía casi nadie: con el CIF ya puesto,
  // `Bienvenida.jsx` rebota a `/`. Ver erp#2102.
  const [form, setForm] = useState({ email: emailFromUrl, password: '', name: '', companyName: '', functionalCurrency: 'EUR', hp_url: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Fuera de `form` a propósito: la rama de alta normal manda `{...form}` por
  // spread, pero las de asesoría y promo construyen el cuerpo campo a campo.
  // Metido en `form`, viajaría solo en una de las tres y las otras dos
  // fallarían con 422 sin que se viera por qué.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Captcha Turnstile, solo en el alta pública y solo si el build trae site key.
  // Sin ella el formulario funciona igual que antes: el backend arranca con
  // CAPTCHA_ENFORCEMENT=off y es quien decide si un alta sin token pasa.
  const turnstileSiteKey = isPromo ? import.meta.env.VITE_TURNSTILE_SITE_KEY : undefined;
  const [captchaToken, setCaptchaToken] = useState('');
  // Cambiarlo remonta el widget y pide token nuevo (cada token vale una vez).
  const [captchaRound, setCaptchaRound] = useState(0);
  const navigate = useNavigate();

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isAccountingFirmInvite) {
        // Accounting-firm flow: query param + body without `token` /
        // companyName / companyCif (advisor doesn't create a company).
        await apiFetch(`/api/auth/register?invitation_token=${encodeURIComponent(invitationToken)}`, {
          method: 'POST',
          body: JSON.stringify({
            email: form.email, password: form.password, name: form.name,
            termsAccepted, termsVersion: LEGAL_VERSION,
          }),
        });
        // Cognito email verification first (/confirm). Tras el primer login,
        // Layout.jsx detecta firmMember=true + sin company activa y redirige
        // a /portal-asesor automáticamente para que el asesor elija qué
        // empresa gestionar primero — sin necesidad de tocar el sidebar.
        navigate('/confirm?email=' + encodeURIComponent(form.email));
      } else if (isPromo) {
        // Alta pública de campaña. Enviamos solo los campos del contrato
        // (incluye el honeypot hp_url, que debe viajar vacío).
        await apiFetch('/api/auth/register-promo', {
          method: 'POST',
          body: JSON.stringify({
            email: form.email,
            password: form.password,
            name: form.name,
            companyName: form.companyName,
            functionalCurrency: form.functionalCurrency,
            hp_url: form.hp_url,
            termsAccepted,
            termsVersion: LEGAL_VERSION,
            // `undefined` desaparece del JSON: sin widget o sin token aún, no viaja.
            captchaToken: captchaToken || undefined,
          }),
        });
        navigate('/confirm?email=' + encodeURIComponent(form.email));
      } else {
        await apiFetch('/api/auth/register', {
          method: 'POST',
          body: JSON.stringify({ ...form, token, termsAccepted, termsVersion: LEGAL_VERSION }),
        });
        navigate('/confirm?email=' + encodeURIComponent(form.email));
      }
    } catch (err) {
      // Cloudflare ya ha gastado el token al verificarlo, falle por lo que falle
      // el alta (email repetido, captcha…): reintentar con el mismo daría 403.
      if (turnstileSiteKey) {
        setCaptchaToken('');
        setCaptchaRound((n) => n + 1);
      }
      setError(
        err.code === 'CAPTCHA_INVALID'
          ? t('register.captcha_invalid', 'No hemos podido comprobar que no eres un robot. Completa la verificación e inténtalo de nuevo.')
          : err.message || t('register.error_fallback', 'Error al crear la cuenta'),
      );
    } finally {
      setLoading(false);
    }
  }

  // WCAG 2.1 AA — autoComplete hints help password managers + a11y tooling.
  // For company sign-up flows, "organization" is the canonical token.
  //
  // Todo campo que entre en este array sale `required` (el atributo está fijo en
  // el <input> del .map de abajo, no se declara por campo). Así fue como el CIF
  // acabó siendo obligatorio sin que nadie lo decidiera. Antes de añadir uno:
  // aquí solo caben campos sin los que no se puede crear la cuenta.
  const fields = [
    { name: 'name',        label: t('register.name_label', 'Nombre completo'),   type: 'text',  icon: User,      placeholder: t('register.name_placeholder', 'Tu nombre'),          autoComplete: 'name' },
    { name: 'email',       label: t('register.email_label', 'Email corporativo'), type: 'email', icon: Mail,      placeholder: t('register.email_placeholder', 'tu@empresa.com'),     autoComplete: 'email', readOnly: !!emailFromUrl },
    ...(!isJoin ? [
      { name: 'companyName', label: t('register.company_label', 'Empresa'),      type: 'text',  icon: Building2, placeholder: t('register.company_placeholder', 'Nombre de tu empresa'), autoComplete: 'organization' },
    ] : []),
  ];

  return (
    <AuthShell maxWidth={460}>
      {isPromo && (
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'center',
            margin: '0 auto 14px', padding: '5px 12px', borderRadius: 999,
            background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))',
            border: '1px solid hsl(var(--primary) / 0.2)', fontSize: 12, fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {t('register.promo_badge', 'Oferta de lanzamiento — prueba gratis')}
        </div>
      )}
      <h2 className="sf-display" style={{ fontSize: 22, fontWeight: 600, color: 'hsl(var(--foreground))', margin: '0 0 3px', letterSpacing: '-0.02em', textAlign: 'center' }}>
        {t('register.title', 'Crear cuenta')}
      </h2>
      <p style={{ fontSize: 14, color: 'hsl(var(--muted-foreground))', margin: '0 0 20px', textAlign: 'center' }}>
        {isPromo ? t('register.subtitle_promo', 'Crea tu cuenta y activa tu prueba gratuita de 30 días (90 si te apuntaste a la lista de espera)') : isJoin ? t('register.subtitle_join', 'Únete a tu equipo en Verix') : t('register.subtitle_default', 'Configura tu espacio de trabajo')}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {/* Honeypot anti-bot — oculto y fuera de tabulación; los humanos nunca lo rellenan. */}
        <input
          type="text"
          name="hp_url"
          value={form.hp_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
        />
        {fields.map(({ name, label, type, icon: Icon, placeholder, readOnly, autoComplete }) => (
          <div key={name}>
            <label style={labelStyle} htmlFor={`register-${name}`}>{label}</label>
            <div style={{ position: 'relative' }}>
              <Icon style={{ ...iconStyle, color: readOnly ? 'hsl(var(--muted-foreground))' : iconStyle.color }} />
              <input
                id={`register-${name}`}
                type={type}
                name={name}
                value={form[name]}
                onChange={handleChange}
                placeholder={placeholder}
                required
                readOnly={readOnly}
                autoComplete={autoComplete}
                className="vx-input"
                style={{ ...inputStyle, background: readOnly ? 'hsl(var(--muted) / 0.7)' : inputStyle.background, color: readOnly ? 'hsl(var(--muted-foreground))' : inputStyle.color }}
              />
            </div>
          </div>
        ))}

        {!isJoin && (
          <div>
            <label style={labelStyle} htmlFor="register-functionalCurrency">{t('register.functional_currency_label', 'Moneda funcional')}</label>
            <div style={{ position: 'relative' }}>
              <Coins style={iconStyle} />
              <select
                id="register-functionalCurrency"
                name="functionalCurrency"
                value={form.functionalCurrency}
                onChange={handleChange}
                className="vx-input"
                style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
              >
                {FUNCTIONAL_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <p style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', margin: '5px 0 0', lineHeight: 1.35 }}>
              {t('register.functional_currency_note', 'Moneda en la que se registra la contabilidad. No se puede cambiar una vez existan asientos.')}
            </p>
          </div>
        )}

        <div>
          <label style={labelStyle} htmlFor="register-password">{t('register.password_label', 'Contraseña')}</label>
          <div style={{ position: 'relative' }}>
            <Lock style={iconStyle} />
            <input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder={t('register.password_placeholder', 'Mínimo 8 caracteres')}
              required
              maxLength={128}
              autoComplete="new-password"
              aria-describedby={error ? 'register-password-rules register-error' : 'register-password-rules'}
              aria-invalid={error ? 'true' : 'false'}
              className="vx-input"
              style={{ ...inputStyle, paddingRight: 48 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('register.hide_password', 'Ocultar contraseña') : t('register.show_password', 'Mostrar contraseña')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'hsl(var(--muted-foreground))' }}
            >
              {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
            </button>
          </div>

          {/*
            Los requisitos, en vivo. Visibles desde el principio —no solo al
            empezar a escribir—: quien los ve antes de elegir contraseña no
            llega a fallar, que es de lo que se trata.

            `aria-live="polite"` en la lista entera y no por item: un lector de
            pantalla anuncia el cambio sin que haya que recorrer la lista, y
            `aria-describedby` en el input ya la lee al enfocar el campo.
            El estado de cada regla viaja también en texto (`sr-only`): en el
            color del icono solo, no existe para quien no lo ve.
          */}
          <ul
            id="register-password-rules"
            aria-live="polite"
            style={{
              listStyle: 'none', margin: '8px 0 0', padding: 0,
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
              gap: '4px 12px',
            }}
          >
            {PASSWORD_RULES.map(({ id, test, defaultLabel }) => {
              const met = test(form.password);
              return (
                <li
                  key={id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, lineHeight: 1.35,
                    color: met ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))',
                    transition: 'color 120ms ease',
                  }}
                >
                  {met
                    ? <Check aria-hidden="true" style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={3} />
                    : <Circle aria-hidden="true" style={{ width: 13, height: 13, flexShrink: 0, opacity: 0.55 }} strokeWidth={2} />}
                  <span>{t(`register.password_rule_${id}`, defaultLabel)}</span>
                  <span className="sr-only">
                    {met ? t('register.password_rule_met', '(cumplido)') : t('register.password_rule_pending', '(pendiente)')}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/*
          Aceptación de términos y privacidad. Va aquí, no en el array `fields`
          de arriba: ese array pinta `<input>` de texto con icono, y meter un
          checkbox rompería su forma. Además el atributo `required` del `.map`
          es lo que un día hizo obligatorio al CIF sin que nadie lo decidiera.

          Con `<label>` nativo y estilos inline, como el resto del formulario:
          esta pantalla usa `AuthShell` y no Tailwind, así que el `Checkbox` de
          shadcn que usa `ConsentDialog` desentonaría.

          Los enlaces van a la landing (`target="_blank"`), donde los textos ya
          están publicados y se mantienen. `rel="noreferrer"` porque abrimos
          pestaña nueva.
        */}
        <label
          style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, lineHeight: 1.45, color: 'hsl(var(--muted-foreground))', cursor: 'pointer', marginTop: 2 }}
        >
          <input
            type="checkbox"
            name="termsAccepted"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required
            style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0, accentColor: 'hsl(var(--primary))', cursor: 'pointer' }}
          />
          <span>
            <Trans
              i18nKey="register.terms_checkbox"
              defaults="He leído y acepto los <1>Términos y condiciones</1> y la <3>Política de privacidad</3>."
              components={{
                1: <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer" style={{ color: 'hsl(var(--primary))', fontWeight: 500 }} />,
                3: <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer" style={{ color: 'hsl(var(--primary))', fontWeight: 500 }} />,
              }}
            />
          </span>
        </label>

        {turnstileSiteKey && (
          <TurnstileWidget key={captchaRound} siteKey={turnstileSiteKey} onToken={setCaptchaToken} />
        )}

        {error && (
          <div
            id="register-error"
            role="alert"
            aria-live="polite"
            style={{ background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--destructive))', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/*
          Doble cierre, y los dos hacen falta. `required` en el checkbox es lo
          que da el aviso nativo del navegador al pulsar sin marcar —el mismo
          comportamiento que el resto de campos de este formulario—, y el
          `disabled` deja claro de un vistazo que falta algo. Sin el `disabled`
          el botón parece activo; sin el `required` no habría mensaje que
          explique por qué no pasa nada.
        */}
        <button
          type="submit"
          disabled={loading || !termsAccepted}
          className="vx-btn-primary"
          style={{ width: '100%', height: 46, borderRadius: 12, border: 'none', color: 'hsl(var(--primary-foreground))', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : (termsAccepted ? 'pointer' : 'not-allowed'), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (loading || !termsAccepted) ? 0.7 : 1, marginTop: 2 }}
        >
          {loading ? (
            <><Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} /> {t('register.creating_account', 'Creando cuenta...')}</>
          ) : (
            <>{t('register.submit', 'Crear cuenta')} <ArrowRight style={{ width: 16, height: 16 }} /></>
          )}
        </button>

        {/* Alta pública con Google — misma ventana que el formulario (isPromo).
            No se ofrece en los flujos de invitación/asesoría: esos ya tienen
            su propio camino con token, y mezclar Google ahí no lo pidió nadie. */}
        {isPromo && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '2px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
              <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap' }}>{t('auth.or_continue_with')}</span>
              <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
            </div>
            <GoogleAuthButton onError={setError} />
          </>
        )}

        <p style={{ textAlign: 'center', fontSize: 13, color: 'hsl(var(--muted-foreground))', margin: '4px 0 0' }}>
          {t('register.have_account', '¿Ya tienes cuenta?')}{' '}
          <Link to="/login" style={{ color: 'hsl(var(--primary))', fontWeight: 500, textDecoration: 'none' }}>
            {t('register.sign_in', 'Iniciar sesión')}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
